import React from "react";

/**
 * StatusChip — QUESTIONNAIRE-2.0-A-S2-β-A (stand-alone).
 *
 * Visual chip representing the translation_status of a single i18n field.
 * NOT yet wired to the Builder — β-B will mount it next to each input.
 *
 * Five visual states (analysis §4):
 *   "draft"          slate    `D`   normal click
 *   "pending_review" amber    `P`   normal click
 *   "reviewed"       emerald  `R`   T1 confirm via parent
 *   "needs_update"   rose     `U`   T1 confirm via parent
 *   "missing"        slate-100 `—`  disabled (Pilot Surveys sin scaffold)
 *
 * The chip never mutates data on its own — it only emits `onClick(nextStatus)`
 * with the proposed next state. The parent decides whether to open the T1
 * dialog or call `useI18nPatch.patch(...)` directly.
 */

const STATE_STYLES = {
  draft:          { bg: "bg-slate-200",  fg: "text-slate-700",  icon: "●", label: "D",  title: "Draft" },
  pending_review: { bg: "bg-amber-100",  fg: "text-amber-900",  icon: "⏳", label: "P", title: "Pending review" },
  reviewed:       { bg: "bg-emerald-100",fg: "text-emerald-900",icon: "✓", label: "R",  title: "Reviewed" },
  needs_update:   { bg: "bg-rose-100",   fg: "text-rose-900",   icon: "⚠", label: "U",  title: "Needs update" },
  missing:        { bg: "bg-slate-100",  fg: "text-slate-400",  icon: "▒", label: "—",  title: "Not scaffolded" },
};

const NEXT_BY_CLICK = {
  draft: "pending_review",
  pending_review: "reviewed",          // requires T1 confirm in parent
  reviewed: "draft",                   // requires T1 demote confirm in parent
  needs_update: "reviewed",            // requires T1 confirm in parent
  missing: null,                       // disabled
};

const REQUIRES_T1 = new Set(["pending_review_to_reviewed", "reviewed_to_draft", "needs_update_to_reviewed"]);

const StatusChip = ({
  status,                // one of: draft | pending_review | reviewed | needs_update | missing | null
  path = "",             // dotted-path (used only for testId derivation)
  langIsBase = false,    // EN/ES/ZH → chip is read-only
  hasScaffold = true,    // non-base + !hasScaffold → disabled
  value = "",            // current field value (for empty-reviewed guard)
  busy = false,          // PATCH in flight
  disabled = false,      // hard external override
  onClick = () => {},    // (next: { from, to, requiresT1 }) => void
  testId,                // optional override
  highlight = false,     // QUESTIONNAIRE-2.0-A-S2-β-D — pulse ring after cascade
}) => {
  const effective = (status === null || status === undefined) ? "missing" : status;
  const style = STATE_STYLES[effective] || STATE_STYLES.missing;
  const next = NEXT_BY_CLICK[effective];

  const isReadOnly = langIsBase;
  const isMissing = effective === "missing" || (!langIsBase && !hasScaffold);
  const isDisabled = disabled || busy || isReadOnly || isMissing || !next;

  // Detect transitions that need T1 confirm dialog in parent.
  const transitionKey = next ? `${effective}_to_${next}` : "";
  const requiresT1 = REQUIRES_T1.has(transitionKey);

  // Empty-value guard: parent should also enforce, but we surface a strong hint.
  const blockedByEmpty = next === "reviewed" && (value || "").trim().length === 0;

  const slug = (path || effective).replace(/[^A-Za-z0-9_]/g, "_");
  const tid = testId || `i18n-status-chip-${slug}`;
  const titleLine =
    isMissing ? "Not scaffolded — initialize i18n scaffold (S3)" :
    isReadOnly ? `Base language ${effective} (implicit reviewed)` :
    blockedByEmpty ? "Cannot mark reviewed: value is empty" :
    next ? `${style.title} → click to mark ${next}` :
    style.title;

  const handleClick = (e) => {
    if (isDisabled || blockedByEmpty || !next) return;
    onClick({ from: effective, to: next, requiresT1, path });
  };

  return (
    <button
      type="button"
      data-testid={tid}
      data-status={effective}
      data-disabled={isDisabled ? "true" : "false"}
      data-highlight={highlight && effective === "needs_update" ? "true" : "false"}
      onClick={handleClick}
      disabled={isDisabled || blockedByEmpty}
      title={titleLine}
      className={`inline-flex items-center gap-1 rounded-md border border-transparent px-1.5 py-0.5 text-[10px] font-semibold transition-colors
        ${style.bg} ${style.fg}
        ${highlight && effective === "needs_update" ? "ring-2 ring-rose-500 ring-offset-1 animate-pulse" : ""}
        ${(isDisabled || blockedByEmpty) ? "opacity-60 cursor-not-allowed" : "hover:ring-1 hover:ring-slate-400 cursor-pointer"}
      `}
    >
      <span aria-hidden>{busy ? "…" : style.icon}</span>
      <span data-testid={`${tid}-label`}>{style.label}</span>
    </button>
  );
};

export const STATUS_CHIP_TRANSITIONS = NEXT_BY_CLICK;
export const STATUS_CHIP_REQUIRES_T1 = REQUIRES_T1;
export default StatusChip;
